'use client';

import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

interface TipoRed {
  id_tipo_red: string;
  nombre: string;
  icono: string | null;
}

export interface RedEmpresa {
  id_tipo_red: string;
  nombre: string;
  url: string;
}

export function useRedesSociales(id_empresa?: string, open?: boolean) {
  const [tiposRed, setTiposRed] = useState<TipoRed[]>([]);
  const [idTipoRed, setIdTipoRed] = useState('');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);

  /* ============================================================
   * Cargar tipos de red cuando el diálogo se abre
   * ============================================================ */
  useEffect(() => {
    if (!open) return;

    const fetchTipos = async () => {
      const { data, error } = await supabase
        .from('tipo_red')
        .select('id_tipo_red, nombre, icono')
        .eq('activo', true)
        .order('nombre');

      if (error) {
        Sentry.captureException(error);
        toast.error('Error al cargar tipos de red');
        return;
      }

      setTiposRed(data || []);
    };

    fetchTipos();
  }, [open]);

  /* ============================================================
   * Precargar datos en modo edición
   * ============================================================ */
  const loadForEdit = useCallback((red: RedEmpresa) => {
    setIdTipoRed(red.id_tipo_red);
    setUrl(red.url);
  }, []);

  /* ============================================================
   * Resetear formulario
   * ============================================================ */
  const resetForm = useCallback(() => {
    setIdTipoRed('');
    setUrl('');
  }, []);

  /* ============================================================
   * Guardar (ADD o UPDATE → upsert)
   * ============================================================ */
  const saveRed = useCallback(async (): Promise<RedEmpresa | null> => {
    if (!id_empresa) {
      toast.error('La empresa no existe');
      return null;
    }

    if (!idTipoRed) {
      toast.error('Selecciona un tipo de red');
      return null;
    }

    if (!url.trim()) {
      toast.error('Introduce una URL válida');
      return null;
    }

    setLoading(true);

    try {
      const { error } = await supabase.from('empresa_redes').upsert(
        {
          id_empresa,
          id_tipo_red: idTipoRed,
          url: url.trim(),
          activo: true,
        },
        { onConflict: 'id_empresa,id_tipo_red' }
      );

      if (error) {
        Sentry.captureException(error, { extra: { id_empresa, idTipoRed } });
        toast.error('No se pudo guardar la red social');
        return null;
      }

      const nombre =
        tiposRed.find((t) => t.id_tipo_red === idTipoRed)?.nombre || '';

      const red: RedEmpresa = {
        id_tipo_red: idTipoRed,
        nombre,
        url: url.trim(),
      };

      resetForm();
      return red;
    } finally {
      setLoading(false);
    }
  }, [id_empresa, idTipoRed, url, tiposRed, resetForm]);

  /* ============================================================
   * ELIMINAR
   * ============================================================ */
  const deleteRed = useCallback(
    async (id_tipo_red: string) => {
      if (!id_empresa) {
        toast.error('Empresa no encontrada');
        return { error: true };
      }

      const { error } = await supabase
        .from('empresa_redes')
        .delete()
        .eq('id_empresa', id_empresa)
        .eq('id_tipo_red', id_tipo_red);

      if (error) {
        Sentry.captureException(error, { extra: { id_empresa, id_tipo_red } });
        toast.error('No se pudo eliminar la red social');
        return { error };
      }

      toast.success('Red social eliminada.');
      return { error: null };
    },
    [id_empresa]
  );

  /* ============================================================
   * Exponer API
   * ============================================================ */
  return {
    tiposRed,
    idTipoRed,
    setIdTipoRed,
    url,
    setUrl,
    loading,
    saveRed,
    deleteRed,
    loadForEdit,
    resetForm,
  };
}
